import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { attendanceApi, type AttendanceStatus } from '@/api/attendance'
import { useAuthStore } from '@/store/authStore'
import { useAttendanceWatchStore } from '@/store/attendanceWatchStore'
import { canAccessLearner } from '@/utils/roleUtils'
import { useLogout } from '@/hooks/useLogout'
import { isLearnerRoutePath } from './learnerRouteGuards'

export const SESSION_ENDED_FLAG = 'lc-session-ended'

/**
 * 교사 '수업 종료'(하원) 또는 자정 AUTO_CLOSE 감지.
 *
 * 단순 OUT은 과거 하원 이력일 수 있으므로 새 로그인 세션을 종료하면 안 된다.
 * 로그인 등원 성공 또는 IN 폴링으로 현재 세션이 출결에 참여했음을 확인한 뒤,
 * 해당 세션이 OUT/AUTO_CLOSE로 닫힌 경우에만 로그아웃한다.
 */
export function shouldAutoLogout(
  status: AttendanceStatus | null | undefined,
  armed: boolean,
  observedOpenAttendanceId?: number | null,
  latestSession?: { attendanceId: number; checkOutAt: string | null } | null,
): boolean {
  if (!armed) return false

  if (observedOpenAttendanceId == null) {
    return (
      status === 'OUT' ||
      (status == null && latestSession?.checkOutAt != null)
    )
  }

  return latestSession?.attendanceId === observedOpenAttendanceId &&
    latestSession.checkOutAt != null
}

/**
 * 학습자 자동 로그아웃 watcher.
 *
 * ① 과거에는 LearnerLayout(탭바 화면)에만 마운트되어 레슨/놀이터/시험 등
 *    풀스크린 라우트에서는 폴링이 멈췄다. 이제 App 루트에 단 한 번 마운트되어
 *    학습자 인증 상태 내내 살아 있으므로 어느 화면에서든 종료를 감지한다.
 */
export function useAttendanceWatcher() {
  const pathname = useLocation().pathname
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userId = useAuthStore((s) => s.user?.id)
  const role = useAuthStore((s) => s.user?.role)
  const watchArmed = useAttendanceWatchStore((s) => s.armed)
  const observedOpenAttendanceId = useAttendanceWatchStore((s) => s.observedOpenAttendanceId)
  const observeOpen = useAttendanceWatchStore((s) => s.observeOpen)
  const logout = useLogout()
  const firedRef = useRef(false)

  const enabled =
    isAuthenticated &&
    userId != null &&
    canAccessLearner(role) &&
    isLearnerRoutePath(pathname)

  const query = useQuery({
    queryKey: ['learner', 'me', 'attendance-watch', userId] as const,
    queryFn: async () => {
      const res = await attendanceApi.getToday()
      return res.data.data
    },
    enabled,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  const status = query.data?.attendanceStatus
  const latestSession = query.data?.latestSession

  // 로그아웃(미인증 전환) 시 다음 로그인 세션을 위해 가드 해제 (전역 단일 마운트라
  // 컴포넌트가 언마운트되지 않으므로 명시적 리셋이 필요하다).
  useEffect(() => {
    if (!isAuthenticated) {
      firedRef.current = false
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!enabled) return
    if (status === undefined) return
    // 새 등원(IN) 관측 시 재무장 — 같은 기기에서 등원→종료가 반복되는 경우 대응.
    if (status === 'IN') {
      firedRef.current = false
      if (
        userId != null &&
        latestSession?.checkOutAt == null &&
        latestSession?.attendanceId != null &&
        (!watchArmed || observedOpenAttendanceId !== latestSession.attendanceId)
      ) {
        observeOpen(userId, latestSession.attendanceId)
      }
      return
    }
    if (
      !firedRef.current &&
      shouldAutoLogout(status, watchArmed, observedOpenAttendanceId, latestSession)
    ) {
      firedRef.current = true
      try {
        sessionStorage.setItem(SESSION_ENDED_FLAG, '1')
      } catch {
        // storage 불가 환경에서도 로그아웃은 진행 (배너는 best-effort)
      }
      logout.mutate()
    }
    // logout.mutate는 렌더마다 새 참조지만 firedRef 가드로 1회만 실행
  }, [
    status,
    latestSession?.attendanceId,
    latestSession?.checkOutAt,
    enabled,
    userId,
    watchArmed,
    observedOpenAttendanceId,
    observeOpen,
  ])
}
